import { useEffect, useState } from "react";
import { documentsService } from "../services/documents";
import type { DocumentType, DocumentHistoryItem } from "../types";

interface Props {
  jobId: string;
  docType: DocumentType;
  latestVersionId: string | null;
  dirty: boolean;
  onRestore: (version: DocumentHistoryItem) => void;
  onPreview: (version: DocumentHistoryItem) => void;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function changeDescription(instruction: string | null): string {
  if (instruction === "__generated__") return "AI generated";
  if (instruction === null) return "Manual edit";
  return `"${instruction}"`;
}

export default function HistoryView({
  jobId,
  docType,
  latestVersionId,
  dirty,
  onRestore,
  onPreview,
}: Props) {
  const [versions, setVersions] = useState<DocumentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    documentsService
      .getHistory(jobId, docType)
      .then((items) => {
        if (!cancelled) setVersions(items);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load history.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [jobId, docType]);

  return (
    <div>
      {loading && (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading history…</p>
      )}

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {!loading && !error && versions.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400 italic">
          No saved versions yet.
        </p>
      )}

      {!loading && versions.length > 0 && (
        <ul className="space-y-3">
          {versions.map((v) => {
            const isCurrent = v.version_id === latestVersionId && !dirty;
            return (
              <li
                key={v.version_id}
                className="flex items-center justify-between gap-4 px-4 py-3 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
              >
                <div className="flex-1 min-w-0">
                  <span className="font-mono text-sm font-semibold text-slate-700 dark:text-slate-300 mr-3">
                    v{v.sequence_number}
                  </span>
                  <span className="text-sm text-slate-500 dark:text-slate-400 mr-3">
                    {formatDate(v.created_at)}
                  </span>
                  <span className="text-sm text-slate-500 dark:text-slate-400 truncate">
                    {changeDescription(v.instruction)}
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => onPreview(v)}
                    className="px-2.5 py-1 text-xs font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                  >
                    Preview
                  </button>

                  {isCurrent ? (
                    <span className="px-2.5 py-1 text-xs font-medium rounded bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
                      Current
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onRestore(v)}
                      className="px-2.5 py-1 text-xs font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                    >
                      Restore
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
